import { Typography } from 'antd';
import type { ReactNode } from 'react';

const { Title, Text } = Typography;

interface PageHeaderProps {
    /**
     * Accent colour for this page, fed to the `--page-header-accent` custom property.
     *
     * The stylesheet mixes it into the gradient and the border, so it is the one thing that
     * distinguishes one page's header from another's. Passed as a value rather than derived
     * from the route: the colours are an editorial choice per page, not a computed one.
     */
    accent: string;

    /** Icon shown in the tinted square. Rendered decorative — the title carries the meaning. */
    icon: ReactNode;

    /** Already translated. This component does no i18n of its own. */
    title: string;

    /** Already translated. */
    subtitle: string;

    /** Optional right-hand actions, such as a primary "add" button. */
    actions?: ReactNode;
}

/**
 * The banner every page opens with: accent, icon, title, subtitle, optional actions.
 *
 * Extracted because the markup had been copied across pages verbatim, which SonarQube
 * flagged between `ConfigurePage` and the former `Settings` page. Copies of pure layout drift quietly — one
 * page gains a heading level or loses the `aria-hidden` on its icon and nothing fails — so
 * the structure lives in one place and the pages supply only what differs.
 *
 * The DOM it renders is byte-for-byte what the pages produced before, because
 * `src/index.css` styles these class names and a Playwright spec reading a page title
 * should not be able to tell this refactor happened.
 */
export default function PageHeader({ accent, icon, title, subtitle, actions }: Readonly<PageHeaderProps>) {
    return (
        <div
            className="page-header"
            style={{ ['--page-header-accent' as string]: accent } as React.CSSProperties}
        >
            <div className="page-header__lead">
                <span className="page-header__icon">{icon}</span>
                <div>
                    <Title level={2} style={{ margin: 0 }}>
                        {title}
                    </Title>
                    <Text type="secondary">{subtitle}</Text>
                </div>
            </div>
            {actions !== undefined && <div className="page-header__actions">{actions}</div>}
        </div>
    );
}
